import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { HOME } from "@/lib/homeContent";
import { trackEvent } from "@/lib/analytics";

const K = { fontFamily: "Kanit, Arial, sans-serif" };
const S = { fontFamily: "'Noto Serif', Georgia, serif" };

// Scrolls to the existing Guide form (#guide) on the current page, or navigates to the Home guide anchor.
export function useGoToGuide() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (placement, extra = {}) => {
    trackEvent("guide_cta_click", { placement, country: "spain", page_path: pathname, ...extra });
    const el = document.getElementById("guide");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      el.focus({ preventScroll: true });
    } else {
      navigate(`${pathname === "/resources" ? "/resources" : "/"}#guide`);
    }
  };
}

// Reusable Free Guide call-to-action. tone: "light" (ivory sections) | "dark" (green/burgundy sections). variant: "inline" | "compact"
export function GuideCta({ placement, tone = "light", variant = "inline", prompt, heading, className = "", onBefore, testId, extra = {} }) {
  const { section } = useSiteContent();
  const c = section("conversion_ctas", HOME.conversion_ctas);
  const go = useGoToGuide();
  const dark = tone === "dark";
  const muted = dark ? "text-white/70" : "text-[#6B4954]";
  const strong = dark ? "text-white" : "text-[#451C25]";
  const onClick = () => { onBefore?.(); go(placement, extra); };

  if (variant === "compact") {
    return (
      <p className={`text-[14px] ${muted} ${className}`} style={S}>
        {prompt ?? c.prompt}{prompt === "" ? "" : " "}
        <button type="button" data-testid={testId || `guide-cta-${placement}`} onClick={onClick} className={`group inline-flex items-center gap-1.5 ${strong} underline underline-offset-4 decoration-[#B3A17D] hover:decoration-current focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B3A17D]`} style={{ ...K, fontWeight: 500 }}>
          {c.button}
          <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
        </button>
      </p>
    );
  }

  return (
    <div data-testid={testId || `guide-cta-${placement}`} className={`border-l-2 border-[#B3A17D] pl-5 ${className}`}>
      <p className={`text-[11px] tracking-[0.26em] uppercase font-semibold ${dark ? "text-[#B3A17D]" : "text-[#897756]"}`} style={K}>{prompt || c.prompt}</p>
      <p className={`mt-2 text-lg md:text-xl leading-snug ${strong}`} style={S}>{heading || c.heading}</p>
      <button type="button" data-testid={`${testId || `guide-cta-${placement}`}-button`} onClick={onClick} className={`group mt-3 inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase font-semibold ${strong} border-b border-[#B3A17D] pb-1 hover:opacity-80 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B3A17D]`} style={K}>
        {c.button}
        <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
      </button>
    </div>
  );
}

export default GuideCta;
